import moment from "moment-timezone";

const PROMOTION_TYPES = {
  AMOUNT_OFF_PRODUCT: "AOP",
  BUY_X_GET_Y: "BXY",
  AMOUNT_OFF_ORDER: "AOO"
};

const APPLY_TO_ALL_CUSTOMERS = "all_customers";

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundMoney(value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

function normalizeId(value) {
  if (value === null || value === undefined) return null;
  return String(value);
}

function normalizeDays(dayOfWeek) {
  if (!dayOfWeek) return [];
  if (Array.isArray(dayOfWeek)) return dayOfWeek.map((day) => String(day));
  return String(dayOfWeek)
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean);
}

function getItemProductId(item) {
  return normalizeId(item.id || item.product?.id || item.product);
}

function getItemCategoryIds(item) {
  if (Array.isArray(item.categories)) {
    return item.categories.map(normalizeId);
  }
  if (Array.isArray(item.product?.categories)) {
    return item.product.categories.map(normalizeId);
  }
  return [];
}

function getLineBaseTotal(item) {
  return roundMoney(toNumber(item.price) * toNumber(item.quantity || 1));
}

function getLineDisplayTotal(item) {
  const modifiersTotal = (
    item.selectedModifiers ||
    item.modifiers ||
    []
  ).reduce((sum, modifier) => sum + toNumber(modifier.price), 0);
  return roundMoney(
    (toNumber(item.price) + modifiersTotal) * toNumber(item.quantity || 1)
  );
}

export function getPromotionEvaluationMoment({
  timezone,
  serveAt,
  orderConfig
} = {}) {
  const zone = timezone || "UTC";
  if (serveAt) {
    const serveAtMoment = moment.tz(serveAt, zone);
    if (serveAtMoment.isValid()) return serveAtMoment;
  }

  const scheduledDay = orderConfig?.scheduledDay;
  const scheduledTime = orderConfig?.scheduledTime;
  let result = moment().tz(zone);

  if (scheduledDay) {
    const normalizedDay = String(scheduledDay)
      .trim()
      .toLowerCase();
    if (normalizedDay === "tomorrow") {
      result = result.add(1, "day");
    } else if (normalizedDay !== "today") {
      const parsedDay = moment.tz(
        scheduledDay,
        ["YYYY-MM-DD", "MM/DD/YYYY", "M/D/YYYY", "dddd, MMMM D", "dddd"],
        zone
      );
      if (parsedDay.isValid()) result = parsedDay;
    }
  }

  if (scheduledTime) {
    const parsedTime = moment(scheduledTime, [
      "HH:mm",
      "H:mm",
      "h:mm A",
      "h:mm a"
    ]);
    if (parsedTime.isValid()) {
      result = result
        .clone()
        .hour(parsedTime.hour())
        .minute(parsedTime.minute())
        .second(0)
        .millisecond(0);
    }
  }

  return result;
}

function isPromotionScheduledNow(promotion, evaluationMoment) {
  if (!promotion?.is_active) return false;
  if (promotion.apply_to && promotion.apply_to !== APPLY_TO_ALL_CUSTOMERS) {
    return false;
  }

  // Filter by sales channel - online orders only see online_order or all channel promotions
  const channel = promotion.sales_channel || "all";
  if (channel !== "all" && channel !== "online_order") {
    return false;
  }

  const currentDate = evaluationMoment.format("YYYY-MM-DD");
  if (promotion.start_date && currentDate < promotion.start_date) return false;
  if (promotion.end_date && currentDate > promotion.end_date) return false;

  const days = normalizeDays(promotion.day_of_week);
  const currentDay = evaluationMoment.format("dddd").toLowerCase();
  if (days.length > 0 && !days.includes(currentDay)) return false;

  if (promotion.start_time && promotion.end_time) {
    const currentTime = evaluationMoment.format("HH:mm:ss");
    const startTime = promotion.start_time;
    const endTime = promotion.end_time;

    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    }
    return currentTime >= startTime || currentTime <= endTime;
  }

  return true;
}

export function getActivePromotions(promotions, options = {}) {
  const evaluationMoment = getPromotionEvaluationMoment(options);
  return (promotions || []).filter((promotion) =>
    isPromotionScheduledNow(promotion, evaluationMoment)
  );
}

// ============================================================================
// Rule Config Helpers
// Evaluators now read directly from rule_config; no legacy synthesis layer.
// ============================================================================

function emptyDiscountResult(cartItems) {
  return {
    items: cartItems.map((item) => ({
      ...item,
      discount: 0,
      wholeDiscount: 0,
      itemTotal: getLineDisplayTotal(item),
      itemTotalWithoutDiscount: getLineDisplayTotal(item),
      promotionLabels: []
    })),
    appliedPromotions: [],
    discountTotal: 0
  };
}

function chooseBetterResult(current, candidate) {
  if (!candidate) return current;
  if (!current || candidate.totalDiscount > current.totalDiscount) {
    return candidate;
  }
  return current;
}

function evaluateAmountOffProduct(promotions, items) {
  let best = null;

  promotions.forEach((promotion) => {
    const config = promotion.rule_config;
    if (!config) return;
    const reward = config.reward || {};
    if (reward.target !== "products") return;
    const condition = config.condition || {};
    if (condition.type === "buys_products") return; // BXY, not AOP

    const value = toNumber(reward.value);
    if (value <= 0) return;
    const isFlatAmount = (reward.discount_mode || "percentage") === "amount";
    const include = reward.include || {};

    const lineDiscounts = {};
    let totalDiscount = 0;

    items.forEach((item, index) => {
      let matches = false;
      const scope = include.scope || "all_products";
      if (scope === "all_products") {
        matches = true;
      } else if (scope === "products") {
        const productIds = (include.products || []).map(String);
        matches =
          productIds.length > 0 &&
          productIds.includes(String(getItemProductId(item)));
      } else if (scope === "categories") {
        const categoryIds = (include.categories || []).map(String);
        const itemCategoryIds = getItemCategoryIds(item).map(String);
        matches =
          categoryIds.length > 0 &&
          categoryIds.some((cid) => itemCategoryIds.includes(cid));
      }
      if (!matches) return;

      const lineBase = getLineBaseTotal(item);
      const unitPrice = toNumber(item.price || 0);
      const itemQuantity = Math.floor(toNumber(item.quantity || 0));
      const discountBase = reward.discount_base || "each_product_price";
      let discountAmount;
      if (isFlatAmount) {
        if (discountBase === "each_product_price" && itemQuantity > 0) {
          discountAmount = Math.min(value, unitPrice) * itemQuantity;
        } else {
          discountAmount = Math.min(value, lineBase);
        }
      } else {
        discountAmount = roundMoney(lineBase * (value / 100));
      }
      lineDiscounts[index] = Math.max(
        lineDiscounts[index] || 0,
        discountAmount
      );
    });

    Object.values(lineDiscounts).forEach((amount) => {
      totalDiscount += amount;
    });

    best = chooseBetterResult(best, {
      promotion,
      lineDiscounts,
      totalDiscount: roundMoney(totalDiscount)
    });
  });

  return best;
}

function buildUnitCandidates(items, matcher) {
  const units = [];
  items.forEach((item, itemIndex) => {
    if (!matcher(item)) return;
    const quantity = Math.floor(toNumber(item.quantity || 0));
    for (let unitIndex = 0; unitIndex < quantity; unitIndex += 1) {
      units.push({
        itemIndex,
        unitPrice: toNumber(item.price)
      });
    }
  });
  return units;
}

function evaluateBuyXGetY(promotions, items) {
  let best = null;

  promotions.forEach((promotion) => {
    const config = promotion.rule_config;
    if (!config) return;
    const reward = config.reward || {};
    const condition = config.condition || {};

    const isBundle = reward.target === "bundle";
    const isBuysProducts =
      condition.type === "buys_products" && reward.target === "products";
    if (!isBundle && !isBuysProducts) return;

    const bundlePrice =
      reward.target === "bundle" ? toNumber(reward.bundle_price) : 0;
    const value = bundlePrice || toNumber(reward.value);
    const getQuantity = Math.floor(toNumber(reward.quantity || 1));
    const buyQuantity = Math.floor(toNumber(condition.threshold || 1));
    if (buyQuantity <= 0 || getQuantity <= 0 || value <= 0) return;

    const conditionInclude = condition.include || {};
    const rewardInclude = reward.include || {};

    const buildMatcher = (scopeConfig) => (item) => {
      const scope = scopeConfig?.scope || "all_products";
      if (scope === "all_products") return true;
      if (scope === "products") {
        const ids = (scopeConfig.products || []).map(String);
        return ids.length > 0 && ids.includes(String(getItemProductId(item)));
      }
      if (scope === "categories") {
        const ids = (scopeConfig.categories || []).map(String);
        const itemCats = getItemCategoryIds(item).map(String);
        return ids.length > 0 && ids.some((cid) => itemCats.includes(cid));
      }
      return false;
    };

    const buyMatcher = buildMatcher(conditionInclude);
    const getMatcher =
      rewardInclude.scope === "same_as_condition"
        ? buyMatcher
        : buildMatcher(rewardInclude);

    const buyUnits = buildUnitCandidates(items, buyMatcher);
    const getUnits = buildUnitCandidates(items, getMatcher);

    const lineDiscounts = {};
    let totalDiscount = 0;

    if (isBundle) {
      const bundleSize = buyQuantity;
      const numBundles = Math.floor(buyUnits.length / bundleSize);
      if (numBundles <= 0) return;

      const sortedBuyUnits = [...buyUnits].sort(
        (a, b) => b.unitPrice - a.unitPrice
      );
      let bundleFullPrice = 0;
      const bundleItemIndexes = [];
      for (let i = 0; i < numBundles * bundleSize; i++) {
        bundleFullPrice += sortedBuyUnits[i].unitPrice;
        bundleItemIndexes.push(sortedBuyUnits[i].itemIndex);
      }
      const bundleDiscount = Math.max(bundleFullPrice - value * numBundles, 0);
      if (bundleDiscount <= 0) return;

      const selectedUnits = [];
      for (let i = 0; i < numBundles * bundleSize; i++) {
        selectedUnits.push(sortedBuyUnits[i]);
      }
      const selectedTotal = selectedUnits.reduce((s, u) => s + u.unitPrice, 0);

      let distributed = 0;
      selectedUnits.forEach((unit, idx) => {
        const isLast = idx === selectedUnits.length - 1;
        const share = isLast
          ? roundMoney(bundleDiscount - distributed)
          : roundMoney(bundleDiscount * (unit.unitPrice / selectedTotal));
        lineDiscounts[unit.itemIndex] = roundMoney(
          (lineDiscounts[unit.itemIndex] || 0) + share
        );
        distributed = roundMoney(distributed + share);
      });
      totalDiscount = distributed;
    } else {
      // buys_products path: check if this is a percentage/flat discount
      // on items (not unit-based "get free")
      const discountMode = reward.discount_mode || reward.discount_type;
      if (discountMode === "percentage" || discountMode === "amount") {
        const conditionItems = items.filter(buyMatcher);
        const metric = condition.metric || "quantity";
        const totalQuantity = conditionItems.reduce(
          (s, i) => s + (parseFloat(i.quantity) || 0),
          0
        );
        const totalAmount = conditionItems.reduce(
          (s, i) => s + (parseFloat(i.original_total_price) || 0),
          0
        );
        const threshold = toNumber(condition.threshold);
        const qualifying = metric === "quantity" ? totalQuantity : totalAmount;
        if (qualifying < threshold) return;

        // Determine which items receive the reward
        let rewardItems;
        if (rewardInclude.scope === "same_as_condition") {
          rewardItems = conditionItems;
        } else {
          rewardItems = items.filter(getMatcher);
        }
        if (rewardItems.length === 0) return;

        items.forEach((item, itemIndex) => {
          const isRewardItem =
            rewardInclude.scope === "same_as_condition"
              ? buyMatcher(item)
              : getMatcher(item);
          if (!isRewardItem) return;

          const itemBase = getLineBaseTotal(item);
          let itemDiscount = 0;
          if (discountMode === "percentage") {
            itemDiscount = roundMoney(itemBase * (value / 100));
          } else {
            itemDiscount = roundMoney(Math.min(value, itemBase));
          }
          if (itemDiscount > 0) {
            totalDiscount += itemDiscount;
            lineDiscounts[itemIndex] = roundMoney(
              (lineDiscounts[itemIndex] || 0) + itemDiscount
            );
          }
        });
        if (totalDiscount <= 0) return;
      } else {
        // Unit-based "get Y free / discounted" logic
        const awardedUnits =
          Math.floor(getUnits.length / (buyQuantity + getQuantity)) *
          getQuantity;
        if (awardedUnits <= 0) return;

        // Choose items to discount
        let candidateUnits;
        if (reward.strategy === "least_expensive") {
          candidateUnits = [...getUnits].sort(
            (a, b) => a.unitPrice - b.unitPrice
          );
        } else if (reward.strategy === "most_expensive") {
          candidateUnits = [...getUnits].sort(
            (a, b) => b.unitPrice - a.unitPrice
          );
        } else {
          // cheapest first by default
          candidateUnits = [...getUnits].sort(
            (a, b) => a.unitPrice - b.unitPrice
          );
        }

        const selectedUnits = candidateUnits.slice(0, awardedUnits);

        selectedUnits.forEach((unit) => {
          lineDiscounts[unit.itemIndex] =
            (lineDiscounts[unit.itemIndex] || 0) + unit.unitPrice;
        });
        totalDiscount = Object.values(lineDiscounts).reduce(
          (sum, val) => sum + val,
          0
        );
      }
    }

    best = chooseBetterResult(best, {
      promotion,
      lineDiscounts,
      totalDiscount: roundMoney(totalDiscount)
    });
  });

  return best;
}

function evaluateAmountOffOrder(promotions, items, existingLineDiscounts) {
  let best = null;

  promotions.forEach((promotion) => {
    const config = promotion.rule_config;
    if (!config) return;
    const reward = config.reward || {};

    if (reward.target !== "order") return;

    const value = toNumber(reward.value);
    if (value <= 0) return;

    const condition = config.condition || {};
    const minOrderAmount =
      condition.type === "order_subtotal" ? toNumber(condition.threshold) : 0;

    const isFlatAmount = (reward.discount_type || "percentage") === "amount";
    const eligibleSubtotal = items.reduce(
      (sum, item) => sum + getLineBaseTotal(item),
      0
    );
    if (eligibleSubtotal < minOrderAmount) return;

    const discountableSubtotal = items.reduce((sum, item, index) => {
      const remaining = Math.max(
        getLineBaseTotal(item) - toNumber(existingLineDiscounts[index]),
        0
      );
      return sum + remaining;
    }, 0);
    const totalDiscount = isFlatAmount
      ? Math.min(value, discountableSubtotal)
      : roundMoney(discountableSubtotal * (value / 100));

    best = chooseBetterResult(best, { promotion, totalDiscount });
  });

  return best;
}

function addAppliedPromotion(appliedPromotions, result, type) {
  if (!result || result.totalDiscount <= 0) return;
  appliedPromotions.push({
    id: result.promotion.id,
    name: result.promotion.name,
    promotion_type: result.promotion.promotion_type,
    type,
    discount: roundMoney(result.totalDiscount)
  });
}

export function applyPromotionsToCart(cartItems, promotions, options = {}) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return emptyDiscountResult([]);
  }

  const eligiblePromotions = getActivePromotions(promotions, options);

  if (eligiblePromotions.length === 0) {
    return emptyDiscountResult(cartItems);
  }

  const items = cartItems.map((item) => ({
    ...item,
    discount: 0,
    wholeDiscount: 0,
    itemTotalWithoutDiscount: getLineDisplayTotal(item),
    itemTotal: getLineDisplayTotal(item),
    promotionLabels: []
  }));

  const aopResult = evaluateAmountOffProduct(
    eligiblePromotions.filter(
      (promotion) =>
        promotion.promotion_type === PROMOTION_TYPES.AMOUNT_OFF_PRODUCT
    ),
    items
  );
  const bxyResult = evaluateBuyXGetY(
    eligiblePromotions.filter(
      (promotion) => promotion.promotion_type === PROMOTION_TYPES.BUY_X_GET_Y
    ),
    items
  );

  const lineDiscounts = {};
  const appliedPromotions = [];

  [aopResult, bxyResult].forEach((result) => {
    if (!result || result.totalDiscount <= 0) return;
    Object.entries(result.lineDiscounts).forEach(([index, discount]) => {
      const lineBase = getLineBaseTotal(items[index]);
      const currentDiscount = toNumber(lineDiscounts[index]);
      lineDiscounts[index] = Math.min(
        roundMoney(currentDiscount + discount),
        lineBase
      );
      items[index].promotionLabels.push(result.promotion.name);
    });
  });

  addAppliedPromotion(appliedPromotions, aopResult, "Item discount");
  addAppliedPromotion(appliedPromotions, bxyResult, "Buy X Get Y");

  const aooResult = evaluateAmountOffOrder(
    eligiblePromotions.filter(
      (promotion) =>
        promotion.promotion_type === PROMOTION_TYPES.AMOUNT_OFF_ORDER
    ),
    items,
    lineDiscounts
  );

  if (aooResult && aooResult.totalDiscount > 0) {
    const eligibleLineIndexes = items
      .map((item, index) => ({
        index,
        remainingLine: Math.max(
          getLineBaseTotal(item) - toNumber(lineDiscounts[index]),
          0
        )
      }))
      .filter((line) => line.remainingLine > 0);
    const remainingSubtotal = eligibleLineIndexes.reduce(
      (sum, line) => sum + line.remainingLine,
      0
    );

    let distributed = 0;
    eligibleLineIndexes.forEach((line, eligibleIndex) => {
      if (remainingSubtotal <= 0) return;
      const item = items[line.index];
      const isLastLine = eligibleIndex === eligibleLineIndexes.length - 1;
      const share = isLastLine
        ? roundMoney(aooResult.totalDiscount - distributed)
        : roundMoney(
            aooResult.totalDiscount * (line.remainingLine / remainingSubtotal)
          );
      item.wholeDiscount = Math.min(share, line.remainingLine);
      distributed = roundMoney(distributed + item.wholeDiscount);
      item.promotionLabels.push(aooResult.promotion.name);
    });

    addAppliedPromotion(appliedPromotions, aooResult, "Order discount");
  }

  let discountTotal = 0;
  items.forEach((item, index) => {
    const itemDiscount = Math.min(
      toNumber(lineDiscounts[index]),
      getLineBaseTotal(item)
    );
    item.discount = roundMoney(itemDiscount);
    item.wholeDiscount = roundMoney(item.wholeDiscount || 0);
    item.itemTotal = roundMoney(
      Math.max(
        getLineDisplayTotal(item) - item.discount - item.wholeDiscount,
        0
      )
    );
    item.itemTotalWithoutDiscount = getLineDisplayTotal(item);
    discountTotal = roundMoney(
      discountTotal + item.discount + item.wholeDiscount
    );
  });

  return {
    items,
    appliedPromotions,
    discountTotal
  };
}
